/**
 * "Role access" — which roles can see one entity, field or table.
 *
 * Every role is ticked to begin with, because everything is visible until an
 * admin says otherwise; unticking a role hides the thing from it. Admin heads
 * the list ticked and greyed out: it always sees everything, and showing that
 * answers "can I lock myself out?" before anyone has to ask.
 *
 * Nothing is saved until Apply. This changes what colleagues can read, so it
 * should not happen because a click landed on the wrong row.
 */
import React from 'react';
import { Alert, Box, Button, ButtonBase, Popover, Stack, Typography } from '@mui/material';
import LockOutlined from '@mui/icons-material/LockOutlined';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';

import { OptionList } from '../../ui-kit';
import { joinNames, type AccessRole, type RoleChange } from './useDataModelAccess';

export interface VisibleToPickerProps {
  /** What is being shown or hidden, for the heading: "Deal", "broker_profit", "6 fields". */
  subject: string;
  roles: AccessRole[];
  hiddenRoleIds: string[];
  /** Roles that see some of it but not all. Shown ticked, with a note. */
  partialRoleIds?: string[];
  /** What "some of it" means here, e.g. "Some fields hidden". */
  partialNote?: string;
  /**
   * Roles that cannot be changed here because the level above hides them:
   * a field of an entity that is itself hidden. Maps role id to the line that
   * explains it.
   */
  lockedRoles?: Record<string, string>;
  /** Shown once the admin has unticked something, before they apply. */
  hidingWarning?: string;
  /**
   * Role id → what ticking that role back would also do, shown once it is
   * ticked and before they apply: "This will also make 1 hidden field
   * visible to Sourcing: broker_profit."
   */
  showingWarnings?: Record<string, string>;
  /** When set, the control is off and this says why. */
  disabledReason?: string;
  onApply: (changes: RoleChange[]) => Promise<void>;
  /**
   * `cell` sits in a table row; `select` is the same control drawn as a
   * full-width dropdown, for use on its own; `button` stands in a toolbar.
   */
  variant?: 'cell' | 'select' | 'button';
  /** For `button`: the text on it, in place of the current state. */
  label?: string;
  loading?: boolean;
}

/** The built-in Admin role's row in the list. Never sent to the server. */
const ADMIN = '__admin__';

export function VisibleToPicker({
  subject,
  roles,
  hiddenRoleIds,
  partialRoleIds = [],
  partialNote = 'Sees part of it',
  lockedRoles = {},
  hidingWarning,
  showingWarnings = {},
  disabledReason,
  onApply,
  variant = 'cell',
  label,
  loading,
}: VisibleToPickerProps) {
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const [visible, setVisible] = React.useState<string[]>([]);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const nameOf = (id: string) => roles.find((r) => r.role_id === id)?.name ?? 'A removed role';
  const hidden = hiddenRoleIds.filter((id) => roles.some((r) => r.role_id === id));
  const choosable = roles.filter((r) => !(r.role_id in lockedRoles));
  const startVisible = React.useMemo(
    () => choosable.filter((r) => !hidden.includes(r.role_id)).map((r) => r.role_id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [roles, hiddenRoleIds, lockedRoles],
  );

  const open = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setVisible(startVisible);
    setError(null);
    setAnchor(e.currentTarget);
  };
  const close = () => { if (!saving) setAnchor(null); };

  const changes: RoleChange[] = choosable
    .filter((r) => visible.includes(r.role_id) !== startVisible.includes(r.role_id))
    .map((r) => ({ role_id: r.role_id, hidden: !visible.includes(r.role_id) }));
  const hidingSomething = changes.some((c) => c.hidden);

  const apply = async () => {
    setSaving(true);
    setError(null);
    try {
      await onApply(changes);
      setAnchor(null);
    } catch (e: any) {
      setError(e.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  // What the closed control says, on one line so every row of a table stays
  // the same height. The first role is named — "All except Sourcing" answers
  // the question where "1 hidden" starts another — and the rest are counted:
  // "All except Sourcing +2". `fullState` names them all, for the hover text
  // and for screen readers; the open list shows each one ticked or not.
  const fullState = hidden.length
    ? `All except ${joinNames(hidden.map(nameOf))}`
    : partialRoleIds.length
      ? `Everyone, ${joinNames(partialRoleIds.map(nameOf))} in part`
      : 'Everyone';
  const state = hidden.length
    ? `All except ${nameOf(hidden[0])}${hidden.length > 1 ? ` +${hidden.length - 1}` : ''}`
    : partialRoleIds.length
      ? `Everyone, ${partialRoleIds.length === 1 ? nameOf(partialRoleIds[0]) : `${partialRoleIds.length} roles`} in part`
      : 'Everyone';
  const restricted = hidden.length > 0 || partialRoleIds.length > 0;

  if (loading) {
    return (
      <Box sx={(t) => ({
        height: t.ds.sizes.control.sm, width: 96, borderRadius: `${t.ds.radius.sm}px`,
        bgcolor: t.tokens.surface.sunken,
      })} />
    );
  }

  if (disabledReason) {
    return (
      <Typography variant="caption" color="text.disabled" title={disabledReason}
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        Everyone
      </Typography>
    );
  }

  return (
    <>
      {variant === 'button' ? (
        <Button variant="outlined" size="small" onClick={open}
          startIcon={<LockOutlined />}
          sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>
          {label ?? state}
        </Button>
      ) : (
        <ButtonBase
          onClick={open}
          aria-label={`Role access: ${fullState}. Change`}
          title={fullState}
          sx={(t) => ({
            display: variant === 'select' ? 'flex' : 'inline-flex',
            width: variant === 'select' ? '100%' : undefined,
            minHeight: variant === 'select' ? t.ds.sizes.control.md : undefined,
            maxWidth: '100%', alignItems: 'center', gap: 0.5,
            px: variant === 'select' ? 1.25 : 0.75, py: 0.25, textAlign: 'left',
            borderRadius: `${t.ds.radius.sm}px`,
            bgcolor: variant === 'select' ? t.palette.background.paper : undefined,
            border: `1px solid ${
              restricted ? t.palette.warning.main
                : variant === 'select' ? t.tokens.border.subtle : 'transparent'}`,
            color: restricted ? t.palette.warning.dark : t.tokens.text.secondary,
            '&:hover': { bgcolor: t.tokens.surface.sunken, borderColor: t.tokens.border.subtle },
          })}
        >
          {restricted && <LockOutlined sx={{ fontSize: 14, flexShrink: 0 }} />}
          <Typography variant={variant === 'select' ? 'body2' : 'caption'} noWrap
            sx={{ flex: variant === 'select' ? 1 : undefined, minWidth: 0 }}>
            {state}
          </Typography>
          <ExpandMoreRounded sx={{ fontSize: 14, flexShrink: 0, opacity: 0.6 }} />
        </ButtonBase>
      )}

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={close}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 320, maxWidth: 'calc(100vw - 32px)' } } }}
      >
        <Box sx={{ px: 1.5, pt: 1.5 }}>
          <Typography variant="subtitle2" sx={{ overflowWrap: 'anywhere' }}>
            Who can see {subject}
          </Typography>
        </Box>

        {choosable.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 2 }}>
            {roles.length === 0
              ? 'There are no roles yet besides Admin. Create one under People, then come back to choose what it sees.'
              : 'No role can be changed here.'}
          </Typography>
        ) : (
          <OptionList
            options={[
              { value: ADMIN, label: 'Admin', detail: 'Always sees everything', disabled: true },
              ...choosable.map((r) => ({
                value: r.role_id,
                label: r.name,
                detail: partialRoleIds.includes(r.role_id) ? partialNote : null,
              })),
            ]}
            value={[ADMIN, ...visible]}
            onChange={(next) => setVisible(next.filter((v) => v !== ADMIN))}
            maxHeight={240}
          />
        )}

        {Object.keys(lockedRoles).length > 0 && (
          <Box sx={(t) => ({ px: 1.5, py: 1, borderTop: `1px solid ${t.tokens.border.subtle}` })}>
            {Object.entries(lockedRoles).map(([id, why]) => (
              <Typography key={id} variant="caption" color="text.secondary" component="div"
                sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
                <LockOutlined sx={{ fontSize: 14, mt: '2px', flexShrink: 0 }} />
                <span><b>{nameOf(id)}</b>: {why}</span>
              </Typography>
            ))}
          </Box>
        )}

        {hidingSomething && hidingWarning && (
          <Alert severity="warning" sx={{ mx: 1.5, mb: 1 }}>{hidingWarning}</Alert>
        )}
        {changes.filter((c) => !c.hidden && showingWarnings[c.role_id]).map((c) => (
          <Alert key={c.role_id} severity="warning" sx={{ mx: 1.5, mb: 1 }}>
            {showingWarnings[c.role_id]}
          </Alert>
        ))}
        {error && <Alert severity="error" sx={{ mx: 1.5, mb: 1 }}>{error}</Alert>}

        <Stack direction="row" justifyContent="flex-end" spacing={1}
          sx={(t) => ({ px: 1.5, py: 1, borderTop: `1px solid ${t.tokens.border.subtle}` })}>
          <Button size="small" color="inherit" onClick={close} disabled={saving}>Cancel</Button>
          <Button size="small" variant="contained" onClick={apply}
            disabled={saving || changes.length === 0}>
            {saving ? 'Saving…' : 'Apply'}
          </Button>
        </Stack>
      </Popover>
    </>
  );
}
